import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Calendar from '../../components/common/Calendar';
import apiClient from '../../utils/api';

const ScheduleContainer = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 2rem;
`;

const CalendarSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
`;

const ScheduleInfo = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 1rem;
  line-height: 1.6;
`;

const ScheduleList = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const ScheduleListTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1.5rem;
`;

const ScheduleItem = styled.div<{ isSelected?: boolean }>`
  padding: 1.25rem;
  border: 2px solid ${props => props.isSelected ? '#000000' : '#dee2e6'};
  border-radius: 12px;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  cursor: pointer;
  background: ${props => props.isSelected ? '#f8f9fa' : 'white'};
  
  &:hover {
    border-color: #000000;
    background: #f8f9fa;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ScheduleDate = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 0.5rem;
`;

const ScheduleDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ScheduleDetail = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #adb5bd;
  font-size: 1rem;
`;

interface Schedule {
  schedule_no: number;
  schedule_date: string;
  schedule_time: string;
  location: string;
  description: string;
}

const MemberSchedule: React.FC = () => {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);

  // 운동일정 목록 조회
  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/schedules');
      if (response.data.success) {
        setSchedules(response.data.data);
      }
    } catch (error) {
      console.error('운동일정 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, []);

  const scheduledDates = schedules.map(s => s.schedule_date);

  const handleDateClick = (dateStr: string) => {
    setSelectedDate(dateStr);
  };

  const handleMonthChange = (newYear: number, newMonth: number) => {
    setCurrentYear(newYear);
    setCurrentMonth(newMonth);
    // TODO: 새로운 월의 일정 불러오기
  };

  return (
    <DashboardLayout title="운동 일정">
      <ScheduleContainer>
        <Title>운동 일정</Title>
        
        <CalendarSection>
          <Calendar
            year={currentYear}
            month={currentMonth}
            scheduledDates={scheduledDates}
            selectedDate={selectedDate}
            onDateClick={handleDateClick}
            onMonthChange={handleMonthChange}
            readOnly={true}
          />
          <ScheduleInfo>
            ✓ 검은색 테두리 날짜는 일정이 있는 날입니다. 클릭하여 상세 정보를 확인할 수 있습니다.
          </ScheduleInfo>
        </CalendarSection>
        
        <ScheduleList>
          <ScheduleListTitle>이번 달 일정 목록</ScheduleListTitle>
          {loading ? (
            <EmptyState>로딩 중...</EmptyState>
          ) : schedules.length > 0 ? (
            schedules
              .filter(s => s.schedule_date.startsWith(`${currentYear}-${String(currentMonth).padStart(2, '0')}`))
              .sort((a, b) => a.schedule_date.localeCompare(b.schedule_date))
              .map(schedule => (
                <ScheduleItem 
                  key={schedule.schedule_no} 
                  isSelected={selectedDate === schedule.schedule_date}
                  onClick={() => handleDateClick(schedule.schedule_date)}
                >
                  <ScheduleDate>📅 {schedule.schedule_date}</ScheduleDate>
                  <ScheduleDetails>
                    <ScheduleDetail>🕐 {schedule.schedule_time}</ScheduleDetail>
                    <ScheduleDetail>📍 {schedule.location}</ScheduleDetail>
                    <ScheduleDetail>📝 {schedule.description}</ScheduleDetail>
                  </ScheduleDetails>
                </ScheduleItem>
              ))
          ) : (
            <EmptyState>이번 달에 등록된 일정이 없습니다.</EmptyState>
          )}
        </ScheduleList>
      </ScheduleContainer>
    </DashboardLayout>
  );
};

export default MemberSchedule;

